import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type { BundleSurrenderSettings } from 'src/apis/surrender-service/types';
import type { SurrenderRootRequest } from 'src/apis/surrender-service/use-surrender-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  surrenderRoot: SurrenderRootRequest;
  bundleId: Uuid | undefined;
};

type ExecuteProps = {
  url: string;
};

export function useBundleSurrenderSettings({
  surrenderRoot,
  bundleId,
}: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<BundleSurrenderSettings>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!surrenderRoot.response || !bundleId) return;
    if (!surrenderRoot.response.data._links.getBundleSurrenderSettings)
      throw new Forbidden();

    const templatedUrl =
      surrenderRoot.response.data._links.getBundleSurrenderSettings.href;
    const url = urlTemplate.parse(templatedUrl).expand({ bundleId });

    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [surrenderRoot.response, bundleId]);

  if (!surrenderRoot.response || !bundleId) return;
  if (!surrenderRoot.response.data._links.getBundleSurrenderSettings)
    throw new Forbidden();

  return request;
}

export type BundleSurrenderSettingsRequest = ReturnType<
  typeof useBundleSurrenderSettings
>;
